/**
 * Makes supplied properties for a type required, while leaving the rest
 * unchanged
 *
 * @template T - The original type
 * @template K - The keys from T to make required
 *
 * @example
 * type Original = { a?: string; b?: number; c?: boolean };
 * type Modified = RequiredProps<Original, 'a' | 'b'>;
 * // result: { a: string; b: number; c?: boolean }
 */
export type RequiredProps<T, K extends keyof T> = Omit<T, K> &
  Required<Pick<T, K>>;

/**
 * A type-safe version of `Object.entries` that preserves the key/value types of
 * the input object.
 *
 * Normally, `Object.entries` returns `[string, any][]` which loses the
 * specific key types of the object. This function asserts the return type as
 * `[keyof T, T[keyof T]][]` so that TypeScript correctly infers both the keys
 * and values.
 */
export const typedObjectEntries = <T extends Record<PropertyKey, unknown>>(
  obj: T
): [keyof T, T[keyof T]][] =>
  // `as` is safe because `Object.entries` returns key/value pairs
  // for all own enumerable properties, matching `[keyof T, T[keyof T]]`
  Object.entries(obj) as [keyof T, T[keyof T]][];

/**
 * A type-safe version of `Object.fromEntries` that preserves the key/value
 * types of the input array of entries.
 *
 * Normally, `Object.fromEntries` returns `any` which loses type information.
 * This function ensures that TypeScript correctly infers the object type.
 */
export const typedObjectFromEntries = <K extends PropertyKey, V>(
  entries: [K, V][]
): Record<K, V> =>
  // `as` cast is safe because all entries are guaranteed to match `[K, V]`
  Object.fromEntries(entries) as Record<K, V>;
